/**
 * change-user-email — pure gate + outcome logic for the ACCEPTED-user email
 * change (miniplan S4; server side is lexserver's S1–S3).
 *
 * Wire: `POST /api/org/:orgId/users/:userId/email/change`, PLATFORM_ADMIN only
 * (D-2). Distinct from the pending-invitation correction in org-admin's
 * `EditUserDialog`, which stays locked for accepted users — that 409 is correct
 * and go-live §8 depends on it holding.
 *
 * ## Why this operation deserves its own friction
 *
 * The address being set becomes a WORKING LOGIN immediately: `admin-verified`
 * means Clerk marks it verified by fiat, with no proof of ownership
 * (`verification.strategy: "admin"`, verified live 2026-08-05). So a typo in
 * the DESTINATION does not fail — it succeeds, and hands the account to
 * whoever owns the address that was typed. That is why the confirm gate here
 * retypes the NEW address rather than some stable identifier: the destination
 * is the dangerous field, not the target.
 *
 * ## Facts the operator needs at the moment of acting (all verified, not assumed)
 *
 * - The user's **password survives** — they keep their credential and simply
 *   sign in with the new address. No reset, no re-invite, no re-accept.
 * - Their **live session survives too** (D-4, settled by lexserver's real-Clerk
 *   exercise): Clerk does NOT auto-revoke on an admin-verified change, and
 *   nothing in Lexiun forces sign-out today. A user working right now keeps
 *   working, on the old credential, until they next sign in.
 * - The **old address is deleted** from Clerk (D-3) and becomes re-invitable.
 *   It is deleted precisely because a retained address stays a verified
 *   secondary, and Clerk permits sign-in with any verified identifier — keeping
 *   it would leave two working logins.
 */

/** Mirrors `ChangeEmailSchema.reason` in `lexserver/src/routes/org.ts`. */
export const REASON_MAX = 500;

export type OwnershipMode = "admin-verified" | "user-verified";

/**
 * `user-verified` is accepted by the route's enum but the service throws
 * `USER_VERIFIED_MODE_NOT_IMPLEMENTED` (501). It is offered in the UI as a
 * DISABLED choice rather than hidden: D-1 ruled both modes exist and that the
 * mode is never selectable by omission, so the operator should see that a
 * safer mode is coming — not silently get the permissive one.
 */
export const IMPLEMENTED_MODES: readonly OwnershipMode[] = ["admin-verified"];

export function isModeImplemented(mode: OwnershipMode): boolean {
  return IMPLEMENTED_MODES.includes(mode);
}

/** Minimal shape this module needs; the real type comes from @lexiun/org-admin. */
export interface ChangeEmailTarget {
  userId: string;
  email: string;
  displayName?: string;
  clerkUserId?: string;
  invitationStatus?: string;
}

/**
 * Is this user in the state the endpoint serves?
 *
 * Mirrors lexserver's `USER_NOT_ACCEPTED` rule: the user must actually have a
 * login to move. An indeterminate row (no status, no `clerkUserId`) is NOT
 * eligible — same "unknown is not permission" stance as `canEditEmail`.
 */
export function canChangeAcceptedEmail(user: ChangeEmailTarget): boolean {
  if (user.invitationStatus === "accepted") return true;
  // Legacy rows predate invitationStatus but a bound Clerk identity still
  // means an accepted login exists.
  return !user.invitationStatus && !!user.clerkUserId;
}

export type EmailValidation = { ok: true; value: string } | { ok: false; reason: string };

/** Deliberately stricter than `z.string().email()` about surrounding space. */
export function validateNewEmail(raw: string, currentEmail: string): EmailValidation {
  const value = raw.trim();
  if (!value) return { ok: false, reason: "Enter the new email address." };
  // Cheap structural check only — the server is the authority.
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return { ok: false, reason: "That does not look like an email address." };
  }
  if (value.toLowerCase() === currentEmail.trim().toLowerCase()) {
    return { ok: false, reason: "That is already this user's address." };
  }
  return { ok: true, value };
}

export type ChangeEmailGate =
  | { canSubmit: true; newEmail: string }
  | { canSubmit: false; reason: string };

/**
 * Decide whether the destructive button is live.
 *
 * The confirm field must match the NEW address exactly (case-insensitively —
 * mail is case-insensitive in practice and a case-only mismatch is a false
 * alarm, not a caught typo).
 */
export function evaluateChangeEmailGate(input: {
  currentEmail: string;
  newEmail: string;
  confirmEmail: string;
  mode: OwnershipMode | null;
  reason: string;
  busy: boolean;
}): ChangeEmailGate {
  if (input.busy) return { canSubmit: false, reason: "Change in progress." };

  if (input.mode === null) {
    return { canSubmit: false, reason: "Choose how the new address is verified." };
  }
  if (!isModeImplemented(input.mode)) {
    return {
      canSubmit: false,
      reason: "User-verified changes are not available yet — use admin-verified.",
    };
  }

  const validation = validateNewEmail(input.newEmail, input.currentEmail);
  if (!validation.ok) return { canSubmit: false, reason: validation.reason };

  if (input.reason.trim().length > REASON_MAX) {
    return { canSubmit: false, reason: `Reason is longer than ${REASON_MAX} characters.` };
  }

  if (input.confirmEmail.trim().toLowerCase() !== validation.value.toLowerCase()) {
    return {
      canSubmit: false,
      reason: "Retype the new address exactly — this address becomes a working login immediately.",
    };
  }

  return { canSubmit: true, newEmail: validation.value };
}

export type ChangeEmailOutcome =
  | { kind: "applied"; email: string; previousEmail?: string; oldClerkAddressDeleted?: boolean }
  | { kind: "not-applied"; requested: string; actual: string }
  | { kind: "unverifiable"; requested: string };

/**
 * Judge what the server ACTUALLY did rather than trusting the 200.
 *
 * Third time this lane ships this shape (`classifyEmailSave` `c874ed6fc`,
 * `classifyRenameSave` `f7d20259a`) and the reason is unchanged: the response
 * passes through schemas that can drop a field, so a cheerful 200 is not
 * evidence the address moved. Here the stakes are higher than either — an
 * operator who believes a login moved when it did not will hand out an address
 * that still does not work.
 */
export function classifyEmailChangeResult(
  requested: string,
  returned: { email?: unknown; previousEmail?: unknown; oldClerkAddressDeleted?: unknown } | null | undefined,
): ChangeEmailOutcome {
  const want = requested.trim().toLowerCase();
  const actual = typeof returned?.email === "string" ? returned.email : null;
  if (actual === null) return { kind: "unverifiable", requested: requested.trim() };
  if (actual.trim().toLowerCase() === want) {
    return {
      kind: "applied",
      email: actual,
      previousEmail: typeof returned?.previousEmail === "string" ? returned.previousEmail : undefined,
      oldClerkAddressDeleted:
        typeof returned?.oldClerkAddressDeleted === "boolean"
          ? returned.oldClerkAddressDeleted
          : undefined,
    };
  }
  return { kind: "not-applied", requested: requested.trim(), actual };
}

/**
 * `unavailable` is deliberately its own thing rather than folded into
 * `terminal`. "This server cannot answer" and "this change failed" need
 * different words in front of an operator — the first says nothing about the
 * user or the address, and telling someone their email change failed when the
 * endpoint simply is not deployed sends them hunting the wrong problem.
 * (Same distinction `platform-api`'s `ROUTE_NOT_FOUND` exists to preserve.)
 */
export type FailureSeverity = "retry" | "fix-input" | "terminal" | "critical" | "unavailable";

export interface FailureAdvice {
  severity: FailureSeverity;
  /** Does pressing the button again stand any chance of helping? */
  retryable: boolean;
  /** Must the caller reload the row — i.e. may state have changed despite the error? */
  reload: boolean;
  headline: string;
}

/**
 * Turn a lexserver error code into what the operator should DO.
 *
 * The code is authoritative; the server's message is rendered verbatim beside
 * this because `mapChangeEmailError` already decides what is safe to expose.
 * The distinction that matters most:
 *
 * - `WRITE_FAILED` — the server says it rolled back. Safe to retry.
 * - `MANUAL_RECONCILIATION_REQUIRED` — partially applied AND rollback failed.
 *   Retrying can only make it worse, and the row may already have moved. This
 *   is the analogue of the pending path's `INVITATION_REISSUE_FAILED`; lexserver's
 *   own map says "never soften this one", so neither does the UI.
 */
export function adviseOnFailure(code: string | null): FailureAdvice {
  switch (code) {
    case "MANUAL_RECONCILIATION_REQUIRED":
      return {
        severity: "critical",
        retryable: false,
        reload: true,
        headline: "Partially applied — do NOT retry. Contact engineering.",
      };
    case "IDENTITY_DESYNC":
      return {
        severity: "terminal",
        retryable: false,
        reload: true,
        headline: "This login record is inconsistent and cannot be changed automatically.",
      };
    case "WRITE_FAILED":
      return {
        severity: "retry",
        retryable: true,
        reload: true,
        headline: "Not saved — rolled back automatically. Safe to try again.",
      };
    case "CLERK_ADD_FAILED":
      return {
        severity: "retry",
        retryable: true,
        reload: false,
        headline: "The identity provider refused the new address.",
      };
    case "DUPLICATE_EMAIL":
    case "CLERK_EMAIL_TAKEN":
    case "VALIDATION_ERROR":
    case "NOT_IMPLEMENTED":
      return {
        severity: "fix-input",
        retryable: true,
        reload: false,
        headline: "That address cannot be used — pick another.",
      };
    case "USER_NOT_ACCEPTED":
      return {
        severity: "terminal",
        retryable: false,
        reload: true,
        headline: "This user has no accepted login — use the pending-invitation correction.",
      };
    case "NOT_FOUND":
      return { severity: "terminal", retryable: false, reload: true, headline: "User not found." };

    // --- Nothing to do with this user or this address -------------------
    //
    // ROUTE_NOT_FOUND is not hypothetical: lexserver ships the endpoint and
    // the running :3200 process can predate it (their standing
    // "committed-not-served" gotcha), in which case an unmatched Hono route
    // answers text/plain 404 and `platform-api` types it as ROUTE_NOT_FOUND.
    // Reporting that as "the change did not complete" would send an operator
    // looking at the user record instead of at the deploy.
    case "ROUTE_NOT_FOUND":
      return {
        severity: "unavailable",
        retryable: false,
        reload: false,
        headline: "This lexserver build does not have the email-change endpoint — it needs a rebuild + restart.",
      };
    case "NON_JSON_RESPONSE":
      return {
        severity: "unavailable",
        retryable: true,
        reload: false,
        headline: "The server did not answer in JSON — it may be restarting.",
      };
    case "SERVICE_UNAVAILABLE":
      return {
        severity: "unavailable",
        retryable: true,
        reload: false,
        headline: "Clerk is not configured on this server, so no email can be changed here.",
      };
    case "FORBIDDEN":
      return {
        severity: "unavailable",
        retryable: false,
        reload: false,
        headline: "This action needs platform-admin scope.",
      };

    case "INTERNAL_ERROR":
    default:
      // Fail closed: an unrecognised code is not evidence that nothing
      // happened, so never imply it is safe to simply try again.
      return {
        severity: "terminal",
        retryable: false,
        reload: true,
        headline: "The change did not complete.",
      };
  }
}

/** Operator-facing sentence for a non-applied outcome. `null` when applied. */
export function describeOutcome(outcome: ChangeEmailOutcome): string | null {
  if (outcome.kind === "applied") return null;
  if (outcome.kind === "not-applied") {
    return (
      `The server accepted the request but the stored address came back as ` +
      `"${outcome.actual}", not "${outcome.requested}". The login was NOT moved — ` +
      `do not give out the new address.`
    );
  }
  return (
    `The server answered OK but returned no user, so the change could not be ` +
    `verified. Reload and confirm the address before telling anyone it moved.`
  );
}
